import { create } from "zustand";
import { getCurrentAmoFieldValue } from "../utils/amoFieldsUtils";
import {
    getChecklistConditionalRequirementState,
    buildConditionalRequiredAmoValuesMap,
    shouldDisableChecklistField,
} from "../utils/checklistConditionalRequired";
import {
    buildAmoPrerequisiteValuesMap,
    getMissingAmoPrerequisiteFieldsForCustomField,
    shouldDisableAmoPrerequisiteField,
} from "../utils/checklistAmoPrerequisites";
import { getAllAmoFields } from "../utils/amoFieldsUtils";

// Дебаунсинг для логирования
let loggingTimeouts = new Map();

const getAmoFieldNamesById = () => {
  return getAllAmoFields().reduce((acc, field) => {
    acc[String(field.id)] = field.name;
    return acc;
  }, {});
};

// Recursive function to calculate completion for a checklist and its descendants
const getCompletionRecursive = (checklistId, allChecklists, hiddenChecklists, amoValuesByFieldId) => {
    if (hiddenChecklists.includes(checklistId)) {
        return { total: 0, selected: 0 }; // Полностью игнорируем скрытый чеклист
    }

    const checklist = allChecklists.find(c => c.id === checklistId);
    if (!checklist) {
        return { total: 0, selected: 0 };
    }

    const requirementState = getChecklistConditionalRequirementState(
        checklist,
        amoValuesByFieldId
    );
    const selectedForThis = requirementState.cappedSelectedCount;
    const totalForThis = requirementState.denominator;

    let totalSubtree = totalForThis;
    let selectedSubtree = selectedForThis;

    const children = allChecklists.filter(c => c.parent === checklistId);
    for (const child of children) {
        const childCompletion = getCompletionRecursive(
            child.id,
            allChecklists,
            hiddenChecklists,
            amoValuesByFieldId
        );
        totalSubtree += childCompletion.total;
        selectedSubtree += childCompletion.selected;
    }

    return { total: totalSubtree, selected: selectedSubtree };
};

export const useChecklistsState = create((set, get) => ({
  checklists: [],
  hiddenChecklists: [],
  
  setChecklists: (checklists) => {
    set({ checklists });
  },
  
  addChecklist: (checklist) => set((state) => ({ 
    checklists: [...state.checklists, checklist] 
  })),
  
  updateChecklist: (updatedChecklist) => set((state) => ({
    checklists: state.checklists.map((checklist) => 
      checklist.id === updatedChecklist.id ? updatedChecklist : checklist
    )
  })),
  
  updateCustomField: (checklistId, customFieldId, updates) => set((state) => ({
    checklists: state.checklists.map((checklist) => {
      if (checklist.id !== checklistId) return checklist;
      
      return {
        ...checklist,
        custom_fields: checklist.custom_fields.map((cf) => {
          if (cf.id === customFieldId) {
            const updatedCf = { ...cf, ...updates };
            
            if (updates.content && cf.type === 'M') {
              if (!updatedCf.content) {
                updatedCf.content = {};
              }
              if (updates.content.values !== undefined) {
                updatedCf.content.values = Array.isArray(updates.content.values) ? updates.content.values : [];
              } else if (!updatedCf.content.values) {
                updatedCf.content.values = [];
              }
            }
            
            if (updates.statusId !== undefined) {
              updatedCf.statusId = updates.statusId;
            }
            
            return updatedCf;
          }
          return cf;
        })
      };
    })
  })),
  
  setHiddenChecklists: (hiddenChecklists) => set({ hiddenChecklists }),

  // Принудительный легкий rerender после изменения AMO-полей в карточке сделки.
  refreshAmoPrerequisiteState: () => set((state) => ({
    checklists: [...state.checklists]
  })),
  
  hideChecklist: (checklistId) => set((state) => ({
    hiddenChecklists: [...state.hiddenChecklists, checklistId]
  })),
  
  showChecklist: (checklistId) => set((state) => ({
    hiddenChecklists: state.hiddenChecklists.filter(id => id !== checklistId)
  })),
  
  getPercentOfSelectedCFs: (checklistId) => {
    const { checklists, hiddenChecklists } = get();
    const amoValuesByFieldId = buildConditionalRequiredAmoValuesMap(
      checklists,
      getCurrentAmoFieldValue
    );
    const completion = getCompletionRecursive(
      checklistId,
      checklists,
      hiddenChecklists,
      amoValuesByFieldId
    );

    if (completion.total === 0) {
      return 100;
    }

    const percent = Math.round((completion.selected / completion.total) * 100);
    
    // Дебаунсинг логирования - логируем только если не все поля заполнены
    if (completion.selected !== completion.total) {
      const timeoutKey = `${checklistId}_${percent}_${completion.selected}`;
      
      // Очищаем предыдущий таймер для этого чеклиста
      if (loggingTimeouts.has(checklistId)) {
        clearTimeout(loggingTimeouts.get(checklistId));
      }
      
      // Устанавливаем новый таймер
      const timeout = setTimeout(() => {
        loggingTimeouts.delete(checklistId);
      }, 300); // 300мс задержка
      
      loggingTimeouts.set(checklistId, timeout);
    }
    
    return percent;
  },
  
  shouldDisableField: (checklistId, customFieldId) => {
    const checklist = get().checklists.find(c => c.id === checklistId);
    if (!checklist || !checklist.custom_fields) return false;
    const conditionalAmoValuesByFieldId = buildConditionalRequiredAmoValuesMap(
      [checklist],
      getCurrentAmoFieldValue
    );
    const prerequisiteAmoValuesByFieldId = buildAmoPrerequisiteValuesMap(
      [checklist],
      getCurrentAmoFieldValue
    );
    return (
      shouldDisableChecklistField(
        checklist,
        customFieldId,
        conditionalAmoValuesByFieldId
      ) ||
      shouldDisableAmoPrerequisiteField(
        checklist,
        customFieldId,
        prerequisiteAmoValuesByFieldId
      )
    );
  },

  // Возвращает текст причины блокировки для подсказки у поля чеклиста.
  getFieldBlockingReason: (checklistId, customFieldId) => {
    const checklist = get().checklists.find(c => c.id === checklistId);
    if (!checklist || !checklist.custom_fields) return null;

    const amoValuesByFieldId = buildAmoPrerequisiteValuesMap(
      [checklist],
      getCurrentAmoFieldValue
    );
    const missingFieldIds = getMissingAmoPrerequisiteFieldsForCustomField(
      checklist,
      customFieldId,
      amoValuesByFieldId
    );
    if (missingFieldIds.length === 0) {
      return null;
    }

    const namesById = getAmoFieldNamesById();
    const missingFieldNames = missingFieldIds.map(
      fieldId => namesById[String(fieldId)] || `AMO поле ${fieldId}`
    );
    return `Заполните AMO-поля: ${missingFieldNames.join(', ')}`;
  }
}));
